import { Controller } from '../utils/controller';
import { useClickOutside } from 'stimulus-use';
import debounce from 'lodash/debounce';
import { setPerlgemCSRFToken } from '../utils/perlgem_csrf';

export default class extends Controller {
  cmsCssClassMapping = {
    'site-email-signup__success-header': [
      'font-headline',
      'w-full',
      'text-center',
      'font-normal',
      'pt-5'
    ],
    'site-email-signup__success-offer_site-email-signup__success-offer--line1': [
      'font-headline',
      'w-full',
      'text-center',
      'font-normal'
    ],
    'site-email-signup__success-offer_site-email-signup__success-offer--line2': [
      'font-headline',
      'w-full',
      'text-center',
      'font-normal',
      'pb-3'
    ],
    'site-email-signup__success-terms': [
      'font-headline',
      'w-full',
      'text-center',
      'font-normal',
      'pb-5'
    ],
    link: [
      'font-headline',
      'w-full',
      'text-center',
      'font-normal',
      'transition-colors',
      'duration-500',
      'hover:underline'
    ],
    'site-email-signup__success-cta': ['font-headline', 'w-full', 'text-center', 'font-normal'],
    'button_button--medium': [
      'border',
      'inline-block',
      'min-w-[350px]',
      'py-2.5',
      'px-5',
      'uppercase',
      'text-black',
      'text-sm',
      'bg-white',
      'transition-colors',
      'duration-500',
      'hover:bg-black',
      'hover:text-white'
    ],
    'site-email-signup__success-reminder': [
      'font-headline',
      'w-full',
      'text-center',
      'font-normal',
      'pt-5'
    ]
  };

  static targets = [
    'alert',
    'emailError',
    'closeFormIcon',
    'phone',
    'phoneChecked',
    'email',
    'emailChecked',
    'emailContent',
    'emailFormatError',
    'errorsContainer',
    'smsContent',
    'smsTermsContent',
    'popup',
    'successContent',
    'headerContent',
    'headerPopup',
    'headerConfirmation',
    'headerForm',
    'headerFormWrapper',
    'emailSignupForm',
    'emailSignupSuccess',
    'emailSignupFail',
    'emailOptinFail',
    'smsSignupForm',
    'smsSignupSuccess',
    'smsSignupFail',
    'alreadySignedUp',
    'gdpr',
    'gdprContainer',
    'gdprCopy',
    'gdprError',
    'responseModal',
    'submitButton'
  ];
  static requiredTargets = [];

  static classes = [
    'hide',
    'borderAlert',
    'flex',
    'borderAlertRegular',
    'hideModal',
    'modalTransition'
  ];
  static requiredClasses = [];

  static values = {
    debounceRate: {
      type: Number,
      default: 250
    },
    hideOnScroll: {
      type: Boolean,
      default: false
    },
    modalTransitionDuration: {
      type: Number,
      default: 300
    }
  };

  // TARGETS
  declare alertTarget: HTMLParagraphElement;
  declare alreadySignedUpTarget: HTMLDivElement;
  declare closeFormIconTarget: HTMLButtonElement;
  declare emailCheckedTarget: HTMLInputElement;
  declare emailContentTarget: HTMLDivElement | undefined;
  declare errorsContainerTarget: HTMLDivElement;
  declare emailErrorTarget: HTMLDivElement;
  declare emailFormatErrorTarget: HTMLDivElement;
  declare emailOptinFailTarget: HTMLDivElement;
  declare emailSignupFailTarget: HTMLDivElement;
  declare emailSignupFormTarget: HTMLDivElement;
  declare emailSignupSuccessTarget: HTMLDivElement;
  declare emailTarget: HTMLInputElement;
  declare gdprContainerTarget: HTMLDivElement;
  declare gdprCopyTarget: HTMLLabelElement;
  declare gdprErrorTarget: HTMLSpanElement;
  declare gdprTarget: HTMLInputElement;
  declare headerConfirmationTarget: HTMLDivElement;
  declare headerContentTarget: HTMLDivElement;
  declare headerFormTarget: HTMLDivElement;
  declare headerFormWrapperTarget: HTMLDivElement;
  declare headerPopupTarget: HTMLDivElement;
  declare phoneCheckedTarget: HTMLInputElement;
  declare phoneTarget: HTMLInputElement;
  declare popupTarget: HTMLDivElement;
  declare responseModalTarget: HTMLDivElement;
  declare readonly hasAlertTarget: boolean;
  declare readonly hasPhoneTarget: boolean;
  declare readonly hasPhoneCheckedTarget: boolean;
  declare readonly hasEmailCheckedTarget: boolean;
  declare readonly hasErrorsContainerTarget: boolean;
  declare readonly hasEmailSignupSuccessTarget: boolean;
  declare readonly hasGdprContainerTarget: boolean;
  declare readonly hasGdprErrorTarget: boolean;
  declare readonly hasGdprTarget: boolean;
  declare readonly hasEmailContentTarget: boolean;
  declare readonly hasEmailErrorTarget: boolean;
  declare readonly hasEmailOptinFailTarget: boolean;
  declare readonly hasEmailSignupFailTarget: boolean;
  declare readonly hasEmailFormatErrorTarget: boolean;
  declare readonly hasHeaderConfirmationTarget: boolean;
  declare readonly hasHeaderContentTarget: boolean;
  declare readonly hasHeaderFormTarget: boolean;
  declare readonly hasHeaderFormWrapperTarget: boolean;
  declare readonly hasHeaderPopupTarget: boolean;
  declare readonly hasGdprCopyTarget: boolean;
  declare readonly hasSubmitButtonTarget: boolean;
  declare readonly hasCloseFormIconTarget: boolean;
  declare readonly hasSmsContentTarget: boolean;
  declare readonly hasSmsTermsContentTarget: boolean;
  declare smsContentTarget: HTMLLabelElement;
  declare smsTermsContentTarget: HTMLDivElement | undefined;
  declare smsSignupFailTarget: HTMLDivElement;
  declare smsSignupFormTarget: HTMLDivElement;
  declare smsSignupSuccessTarget: HTMLDivElement;
  declare successContentTarget: HTMLDivElement;
  declare submitButtonTarget: HTMLButtonElement;

  // VALUE
  declare hideOnScrollValue: boolean;
  declare debounceRateValue: string;
  declare modalTransitionDurationValue: number;

  // CLASSES
  declare borderAlertClasses: string[];
  declare modalTransitionClasses: string[];
  declare borderAlertRegularClass: string;
  declare borderAlertClass: string;
  declare flexClass: string;
  declare hideModalClass: string;

  // LOCAL STATE
  private debouncedHideContainerOnScroll;
  private hideClass: string;

  connect() {
    useClickOutside(this);
  }

  clickOutside() {
    if (this.hasSmsTermsContentTarget) {
      this.smsTermsContentTarget?.classList.add(this.hideClass);
    }
  }

  initialize() {
    if (this.hideOnScrollValue) {
      this.debouncedHideContainerOnScroll = debounce(
        this.hideContainerOnScroll,
        this.debounceRateValue
      ).bind(this);
      document.addEventListener('scroll', this.debouncedHideContainerOnScroll);
    }
  }

  disconnect() {
    document.removeEventListener('scroll', this.debouncedHideContainerOnScroll);
  }

  hideContainerOnScroll() {
    if (window.scrollY > 0) {
      this.headerContentTarget.classList.add(this.hideClass);
    } else {
      this.headerContentTarget.classList.remove(this.hideClass);
    }
  }

  clearHeaderSignup() {
    if (this.hasHeaderContentTarget) {
      this.headerContentTarget.classList.add(this.hideClass);
      this.addClearanceCookie();
      document.removeEventListener('scroll', this.debouncedHideContainerOnScroll);
    }
  }

  addClearanceCookie() {
    document.cookie = 'welcome15=1; path=/';
  }

  clearEmailInputClass() {
    this.emailTarget.classList.remove(...this.borderAlertClasses);
  }

  clearHeaderForm() {
    if (this.hasHeaderFormTarget && this.hasHeaderConfirmationTarget) {
      this.headerFormTarget.classList.add(this.hideClass);
      if (this.hasHeaderFormWrapperTarget) {
        this.headerFormWrapperTarget.classList.add(this.hideClass);
      }
      this.headerConfirmationTarget.classList.remove(this.hideClass);
      this.headerConfirmationTarget.classList.add(this.flexClass);
      this.addClearanceCookie();
    }
  }

  headerFormSubmit(event) {
    event.preventDefault();
    this.validateFormAndSubmit(event) && this.clearHeaderForm();
  }

  toggleHeaderPopup() {
    if (this.hasHeaderPopupTarget) {
      this.headerPopupTarget.classList.toggle(this.hideClass);
    }
  }

  toggleSignUpSuccess() {
    if (this.popupTarget.classList.contains(this.hideClass)) {
      this.formatCmsContent(this.successContentTarget);
    }
    this.popupTarget.classList.toggle(this.hideClass);
    if (this.hasGdprContainerTarget) {
      this.gdprContainerTarget.classList.toggle(this.hideClass);
    }
  }

  formatCmsContent(element) {
    const elements = element.children;
    for (let i = 0; i < elements.length; i++) {
      const child = elements[i];
      const key = [...child.classList].join('_');
      if (Object.prototype.hasOwnProperty.call(this.cmsCssClassMapping, key)) {
        child.removeAttribute('class');
        child.classList.add(...this.cmsCssClassMapping[key]);
      }
      if (child.children.length > 0) {
        this.formatCmsContent(child);
      }
    }
  }

  toggleEmailPopup() {
    this.popupTarget.classList.toggle(this.hideClass);
  }

  removeDropDown(event) {
    const currentTarget = event.target.getAttribute('data-signup-target');
    if (currentTarget != 'popup') {
      return;
    }
    this.toggleSignUpSuccess();
  }

  validateFormAndSubmit(event) {
    const email = this.emailTarget.value.trim();
    const phone = this.hasPhoneTarget ? this.phoneTarget.value.trim() : '';
    const phoneChecked = this.hasPhoneCheckedTarget ? (this.phoneCheckedTarget.checked ? 1 : 0) : 0;
    const emailChecked = this.hasEmailCheckedTarget ? (this.emailCheckedTarget.checked ? 1 : 0) : 1;
    const regionId = event.currentTarget.dataset.regionId;
    if (email === '' && phone === '') {
      if (this.hasAlertTarget) {
        this.alertTarget.classList.remove(this.hideClass);
      } else {
        this.emailTarget.classList.add(this.borderAlertClass);
      }
      event.preventDefault();
      return false;
    }
    if (!email.includes('@')) {
      this.emailTarget.classList.add(this.borderAlertClass);
      const tgt = this.emailErrorTarget;
      const hideAttr = this.hideClass;
      tgt.classList.remove(this.hideClass);
      setTimeout(function () {
        tgt.classList.add(hideAttr);
      }, 4000);
      return false;
    }
    if (this.hasAlertTarget) {
      this.alertTarget.classList.add(this.hideClass);
    }
    this.emailTarget.classList.remove(this.borderAlertClass);

    if (this.hasEmailCheckedTarget && !this.emailCheckedTarget.checked) {
      this.displayEmailSubmitErrorMessage(emailChecked);
      return false;
    }

    if (this.hasGdprTarget && !this.gdprTarget.checked) {
      this.gdprErrorTarget.classList.remove(this.hideClass);
      return false;
    }

    this.getPerlGemToken().then((token) => {
      this.submitForm(email, phone, emailChecked, phoneChecked, token, regionId);
    });

    return true;
  }

  showEmailContent() {
    if (this.hasEmailContentTarget) {
      this.emailContentTarget?.classList.remove(this.hideClass);
    }
  }

  showSmsContent() {
    if (this.hasSmsContentTarget) {
      this.smsContentTarget?.classList.remove(this.hideClass);
    }
  }

  showSmsTermsContent(event) {
    if (this.hasSmsTermsContentTarget) {
      event.preventDefault();
      this.smsTermsContentTarget?.classList.remove(this.hideClass);
    }
  }

  hideSmsTermsContent(event) {
    if (this.hasSmsTermsContentTarget) {
      event.preventDefault();
      this.smsTermsContentTarget?.classList.add(this.hideClass);
    }
  }

  async getPerlGemToken() {
    const formData = new URLSearchParams();

    formData.append(
      'JSONRPC',
      JSON.stringify([
        {
          method: 'csrf.getToken',
          params: [{}]
        }
      ])
    );

    const response = await fetch('/rpc/jsonrpc.tmpl?dbgmethod=csrf.getToken', {
      method: 'POST',
      body: formData
    })
      .then(async (res) => await res.json())
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error);
      });

    return response[0].result.value.token;
  }

  submitForm(email, phone, emailChecked, phoneChecked, token, regionId) {
    const formData = new URLSearchParams();
    const params = {
      _SUBMIT: 'email_signup',
      PC_EMAIL_PROMOTIONS: emailChecked,
      PC_EMAIL_PROMOTIONS_PRESENT: '1',
      SMS_PROMOTIONS_PRESENT: '1',
      _SECONDARY_SUBMIT: 'sms',
      PC_EMAIL_ADDRESS: email,
      MOBILE_PHONE: phone,
      _TOKEN: token,
      SMS_PROMOTIONS: phoneChecked
    };

    if (regionId && regionId !== '0') {
      params['COUNTRY_ID'] = regionId;
    }

    formData.append(
      'JSONRPC',
      JSON.stringify([
        {
          method: 'rpc.form',
          params: [params]
        }
      ])
    );

    setPerlgemCSRFToken(token);

    fetch('/rpc/jsonrpc.tmpl?dbgmethod=rpc.form', {
      method: 'POST',
      body: formData,
      mode: 'cors'
    })
      .then((response) => {
        response.json();
        this.clearFormErrors();
        this.toggleSignUpSuccess();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }

  async emailFormSubmit(event) {
    if (event?.params?.preventDefault === true) {
      event.preventDefault();
    }

    const email = this.emailTarget.value.trim();
    const emailOptin = this.emailCheckedTarget?.checked ? 1 : 0;
    const token = await this.getPerlGemToken();
    const emailFormData = this.generateEmailFormData(email, emailOptin, token);
    const [jsonrpcResponse] = await this.submitJSONRPC(token, emailFormData);
    if (jsonrpcResponse?.error) {
      const message = jsonrpcResponse.error?.data?.messages?.[0].text;
      this.displayEmailSubmitErrorMessage(emailOptin, message);
    } else if (!emailOptin) {
      this.displayEmailSubmitErrorMessage(emailOptin);
    } else {
      this.displayEmailSubmitSuccessMessage();
    }
  }

  generateEmailFormData(email, emailPromotions, token) {
    const formData = new URLSearchParams();
    formData.append(
      'JSONRPC',
      JSON.stringify([
        {
          method: 'rpc.form',
          params: [
            {
              _SUBMIT: 'email_signup',
              PC_EMAIL_PROMOTIONS: emailPromotions,
              PC_EMAIL_PROMOTIONS_PRESENT: '1',
              PC_EMAIL_ADDRESS: email,
              _TOKEN: token
            }
          ]
        }
      ])
    );
    return formData;
  }

  async smsFormSubmit() {
    const rawPhoneNumber = this.phoneTarget.value.trim();
    const phoneNumber = rawPhoneNumber.replace(/\D/g, '');
    const smsPromotions = this.phoneCheckedTarget.checked ? 1 : 0;
    const token = await this.getPerlGemToken();

    const [subscribeResponse] = await this.subscribeSMSUser(phoneNumber, smsPromotions, token);
    if (subscribeResponse?.error) {
      const message = subscribeResponse.error?.data?.messages?.[0].text;
      this.displaySMSPhoneError(message);
    } else {
      this.displaySMSSubscribeSuccess();
    }
    return true;
  }

  displayEmailSubmitErrorMessage(emailOptin, message = '') {
    if (this.hasEmailSignupSuccessTarget) {
      this.emailSignupSuccessTarget.classList.add(this.hideClass);
    }

    if (message && this.hasEmailSignupFailTarget) {
      this.emailSignupFailTarget.innerHTML = message;
      this.emailSignupFailTarget.classList.remove(this.hideClass);
    }

    if (!emailOptin) {
      this.hasEmailOptinFailTarget && this.emailOptinFailTarget.classList.remove(this.hideClass);
    } else {
      this.hasEmailOptinFailTarget && this.emailOptinFailTarget.classList.add(this.hideClass);
    }
  }

  async gdprToggleCopy(event) {
    event.preventDefault();
    if (this.hasGdprCopyTarget) {
      this.gdprCopyTarget.classList.toggle(this.hideClass);
    }
  }

  async handleSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      const disabled = this.submitButtonTarget.disabled;
      this.submitButtonTarget.disabled = !disabled;
    }
  }

  async toggleCloseFormIcon() {
    if (this.hasCloseFormIconTarget) {
      this.closeFormIconTarget.classList.toggle(this.hideClass);
    }
  }

  async clearFormErrors() {
    this.hide.bind(this);

    if (this.hasEmailErrorTarget) {
      this.hide(this.emailErrorTarget);
    }

    if (this.hasEmailOptinFailTarget) {
      this.hide(this.emailOptinFailTarget);
    }

    if (this.hasGdprErrorTarget) {
      this.hide(this.gdprErrorTarget);
    }

    if (this.hasEmailFormatErrorTarget) {
      this.hide(this.emailFormatErrorTarget);
    }

    if (this.hasErrorsContainerTarget) {
      this.hide(this.errorsContainerTarget);
    }

    this.clearEmailInputClass();
  }

  displayEmailSubmitSuccessMessage() {
    this.emailSignupFormTarget.classList.add(this.hideClass);
    this.emailSignupSuccessTarget.classList.remove(this.hideClass);
    this.emailSignupFailTarget.classList.add(this.hideClass);
    this.emailOptinFailTarget.classList.add(this.hideClass);
  }

  displaySMSPhoneError(message) {
    this.smsSignupSuccessTarget.classList.add(this.hideClass);
    this.smsSignupFailTarget.innerHTML = message;
    this.smsSignupFailTarget.classList.remove(this.hideClass);
  }

  displaySMSSubscribeSuccess() {
    this.smsSignupSuccessTarget.classList.remove(this.hideClass);
    this.smsSignupFailTarget.classList.add(this.hideClass);
    this.smsSignupFormTarget.classList.add(this.hideClass);
  }

  async subscribeSMSUser(phone, smsPromotions, token) {
    const formData = new URLSearchParams();
    formData.append(
      'JSONRPC',
      JSON.stringify([
        {
          method: 'rpc.form',
          id: 7,
          params: [
            {
              _SUBMIT: 'sms_signup',
              SMSPROMO_LAST_SOURCE: 'gnav',
              SMSPROMO_ORIGINAL_SOURCE: 'gnav',
              SMSPROMO_SMS_PROMOTIONS_PRESENT: '1',
              'FIELD_PREFIX--sms_signup': 'SMSPROMO_',
              SMS_AFFILIATE_ID: '',
              SMSPROMO_CARRIER_CODE: 'UNKNOWN',
              SMSPROMO_COUNTRY_ID: '1',
              SMSPROMO_LANGUAGE_ID: '48',
              SMSPROMO_MOBILE_NUMBER: phone,
              SMSPROMO_SMS_PROMOTIONS: smsPromotions,
              _TOKEN: token
            }
          ]
        }
      ])
    );
    const response = await this.submitJSONRPC(token, formData);
    return response;
  }

  async submitJSONRPC(token, formData) {
    setPerlgemCSRFToken(token);

    return await fetch('/rpc/jsonrpc.tmpl?dbgmethod=rpc.form', {
      method: 'POST',
      body: formData,
      mode: 'cors'
    })
      .then(async (res) => await res.json())
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }

  isValidEmail(email) {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return emailRegex.test(email);
  }

  show(target) {
    target.classList.remove(this.hideClass);
  }

  hide(target) {
    target.classList.add(this.hideClass);
  }

  setInvalidEmail() {
    this.emailTarget.classList.add(...this.borderAlertClasses);
    this.emailTarget.classList.remove(this.borderAlertRegularClass);
  }

  async emailSignUp(event) {
    event.preventDefault();
    const email = this.emailTarget.value.trim();
    const emailHasErrors = !this.isValidEmail(email);

    if (emailHasErrors) {
      this.emailErrorTarget.classList.remove(this.hideClass);
      this.setInvalidEmail();
      return false;
    }

    const token = await this.getPerlGemToken();
    const emailFormData = this.generateEmailFormData(email, '', token);
    const [jsonrpcResponse] = await this.submitJSONRPC(token, emailFormData);

    if (jsonrpcResponse?.error) {
      this.emailErrorTarget.classList.remove(this.hideClass);
      return false;
    }

    const previouslyOptedInEmailPromotion =
      jsonrpcResponse.result.data.userinfo.previously_opted_in_email_promotion;
    this.headerFormTarget.classList.add(this.hideClass);
    if (this.hasHeaderFormWrapperTarget) {
      this.headerFormWrapperTarget.classList.add(this.hideClass);
    }

    if (previouslyOptedInEmailPromotion) {
      this.alreadySignedUpTarget.classList.remove(this.hideClass);
      this.closeFormIconTarget.classList.remove(this.hideClass);
    } else {
      this.headerConfirmationTarget.classList.remove(this.hideClass);
      this.closeFormIconTarget.classList.remove(this.hideClass);
      this.addClearanceCookie();
    }
  }

  async signupLoyaltyForm(event) {
    event.preventDefault();
    const email = this.emailTarget.value.trim();
    const emailIsEmpty = email === '';
    const emailIsValid = this.isValidEmail(email) && !emailIsEmpty;
    const gdprIsChecked = this.hasGdprTarget && this.gdprTarget.checked;

    if (emailIsEmpty || !emailIsValid) {
      this.setInvalidEmail();
    }

    if (!gdprIsChecked) {
      this.show(this.gdprErrorTarget);
    }

    if (emailIsEmpty) {
      this.show(this.emailErrorTarget);
    }

    if (!emailIsEmpty && !emailIsValid) {
      this.show(this.emailFormatErrorTarget);
    }

    if (emailIsEmpty || !emailIsValid || !gdprIsChecked) {
      this.show(this.errorsContainerTarget);
      return false;
    }

    const token = await this.getPerlGemToken();
    const emailFormData = this.generateEmailFormData(email, '', token);
    const [jsonrpcResponse] = await this.submitJSONRPC(token, emailFormData);

    if (jsonrpcResponse?.error) {
      return false;
    }

    this.openModal();
  }

  buildModalTransitionClasses() {
    const durationClass = `duration-${this.modalTransitionDurationValue}`;

    return [...this.modalTransitionClasses, durationClass];
  }

  openModal() {
    this.resetModal();
    this.show(this.responseModalTarget);
    this.responseModalTarget.classList.add(...this.buildModalTransitionClasses());
  }

  closeModal() {
    this.responseModalTarget.classList.add(this.hideModalClass);

    setTimeout(() => {
      this.resetModal();
    }, 300);
  }

  resetModal() {
    this.hide(this.responseModalTarget);
    this.responseModalTarget.classList.remove(...this.buildModalTransitionClasses());
    this.responseModalTarget.classList.remove(this.hideModalClass);
  }

  isEnterPressed(event) {
    return event.key === 'Enter';
  }

  submitEmailOnEnter(event) {
    if (this.isEnterPressed(event)) {
      this.emailFormSubmit(event);
    }
  }

  submitSMSOnEnter(event) {
    if (this.isEnterPressed(event)) {
      this.smsFormSubmit();
    }
  }
}
